import { Link } from "react-router-dom";
import { Unplug } from "lucide-react";

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;

const Y_TICKS = ["$200k", "$150k", "$100k", "$50k", "$0k"] as const;

const DEFAULT_LEGEND: { code: string; color: string }[] = [
  { code: "ILS", color: "#1447e6" },
  { code: "USD", color: "#27ae60" },
  { code: "EUR", color: "#eb8934" },
];

type Props = {
  ctaTo: string;
  ctaLabel?: string;
  legend?: { code: string; color: string }[];
  /** When false, omit the dashed plot grid (e.g. very narrow layouts). */
  showGrid?: boolean;
  /** When false, hide the bottom legend row. */
  showLegend?: boolean;
};

export function LineChartEmptyContent({
  ctaTo,
  ctaLabel = "Connect your accounts to view",
  legend = DEFAULT_LEGEND,
  showGrid = true,
  showLegend = true,
}: Props) {
  return (
    <div className="flex w-full flex-col">
      <div className="relative h-[280px] w-full min-w-0 overflow-hidden">
        <div className="absolute inset-y-0 left-0 flex w-9 flex-col justify-between py-1 pr-1 text-right text-[11px] text-[#8e95a2]">
          {Y_TICKS.map((label) => (
            <span key={label} className="leading-none">
              {label}
            </span>
          ))}
        </div>

        <div className="absolute inset-y-0 left-9 right-0 pl-1">
          {showGrid ? (
            <svg
              className="absolute inset-0 h-full w-full text-slate-200"
              aria-hidden
            >
              {Array.from({ length: 5 }, (_, i) => {
                const y = (i / 4) * 100;
                return (
                  <line
                    key={`h-${i}`}
                    x1="0%"
                    y1={`${y}%`}
                    x2="100%"
                    y2={`${y}%`}
                    stroke="currentColor"
                    strokeWidth={1}
                    strokeDasharray="4 4"
                  />
                );
              })}
              {Array.from({ length: 12 }, (_, i) => {
                const x = (i / 11) * 100;
                return (
                  <line
                    key={`v-${i}`}
                    x1={`${x}%`}
                    y1="0%"
                    x2={`${x}%`}
                    y2="100%"
                    stroke="currentColor"
                    strokeWidth={1}
                    strokeDasharray="4 4"
                  />
                );
              })}
            </svg>
          ) : null}

          <div className="pointer-events-none absolute inset-x-0 bottom-0 flex h-5 items-end justify-between text-[11px] leading-none text-[#8e95a2]">
            {MONTHS.map((m) => (
              <span key={m} className="min-w-0 flex-1 text-center">
                {m}
              </span>
            ))}
          </div>

          <div className="absolute inset-x-0 top-0 bottom-5 flex items-center justify-center px-2">
            <Link
              to={ctaTo}
              className="inline-flex h-16 max-w-full items-center gap-2 rounded-lg border border-[#3b82f6] bg-white px-4 text-sm font-medium text-[#155dfc] shadow-sm sm:px-6"
            >
              <Unplug className="h-6 w-6 shrink-0" aria-hidden />
              <span className="text-center leading-tight sm:whitespace-nowrap">
                {ctaLabel}
              </span>
            </Link>
          </div>
        </div>
      </div>

      {showLegend ? (
        <div className="mt-2 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-sm">
          {legend.map((item) => (
            <span
              key={item.code}
              className="inline-flex items-center gap-1 font-normal"
              style={{ color: item.color }}
            >
              <span
                className="h-3.5 w-3.5 shrink-0 rounded-full"
                style={{ backgroundColor: item.color }}
                aria-hidden
              />
              {item.code}
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}

type CardProps = Props & {
  title: string;
  headerLinkLabel?: string;
  headerLinkTo?: string;
};

export function LineChartEmptyCard({
  title,
  headerLinkLabel = "Manage Accounts",
  headerLinkTo = "/files-integrations",
  ...contentProps
}: CardProps) {
  return (
    <div className="rounded-[14px] border border-[#e8ecf1] bg-white p-4 shadow-[0px_1px_1.5px_rgba(0,0,0,0.06)]">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-2">
        <h3 className="text-base font-semibold leading-6 text-[#0f172a]">
          {title}
        </h3>
        {headerLinkTo ? (
          <Link
            to={headerLinkTo}
            className="text-sm font-medium text-[#155dfc] hover:underline"
          >
            {headerLinkLabel}
          </Link>
        ) : null}
      </div>
      <LineChartEmptyContent {...contentProps} />
    </div>
  );
}
